/* Treatment rows: regroup existing content without rewriting clinical instructions.
   Care advice in the featured text («Før behandling» / «Etter behandling» headings) becomes ONE row,
   «Før og etter behandling», with the two headings kept as subheadings. Nodes are moved intact, never
   rebuilt; unknown formats stay where they are, and nothing is dropped except empty paste paragraphs and the
   «Slik virker behandlingen» row when the band under the product already shows the same text. */
(() => {
  // The end of the sentence only, as in the Liquid: the opening words change with admin copy fixes.
  const CARE_INTRO = /bivirkninger, (?:følg|ber vi deg følge) disse rådene nøye:$/i;
  const CARE_HEADING = /^(Før|Etter) behandling(?:en)?:?$/i;
  const PHONE_HELP = 'Finner du ikke ledig tid på ønsket dag eller tidspunkt, er du velkommen til å ringe oss på 458 01 000. Vi har enkelte tilgjengelige timer som ikke ligger ute på nett, og hjelper deg gjerne med å finne en passende time.';
  const text = node => (node.textContent || '').replace(/\s+/g, ' ').trim();
  // samlet_tekst: sentences written to the reader, course and interval sentences, and the words every text has (the
  // same list as sections/laserlab-treatment-story.liquid).
  const READER = /(^|[^\p{L}])(du|deg|din|ditt|dine)(?![\p{L}])/iu;
  const COURSE = /vi anbefaler|anbefalt kur|hver \d+\.? uke/i;
  const STOP = new Set('behan,huden,også,eller,etter,under,over,uten,helt,bare,mens,både,flere,dette,denne,slik,hver,alle,noen,blir,gjøre,gjør'.split(','));

  function row(label) {
    const item = document.createElement('details');
    item.className = 'll-treatment-panel';
    const summary = document.createElement('summary');
    summary.textContent = label;
    const toggle = document.createElement('span');
    toggle.className = 'll-accordion__toggle';
    toggle.setAttribute('aria-hidden', 'true');
    summary.append(toggle);
    const content = document.createElement('div');
    content.className = 'll-accordion__panel rte';
    item.append(summary, content);
    return { item, content };
  }

  function enhance(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = 'true';
    const description = root.querySelector('[data-treatment-description]');
    const featured = root.querySelector('[data-treatment-featured]');
    const explanation = root.querySelector('[data-treatment-explanation]');

    // Empty paste artifacts add large gaps but carry no information.
    [description, featured].filter(Boolean).forEach(source => {
      Array.from(source.children).forEach(node => {
        if (node.tagName === 'P' && !text(node) && !node.querySelector('img, video, iframe')) node.remove();
      });
    });

    // Availability help: kept, as its own short row instead of a long paragraph in the description.
    let booking = null;
    if (description) {
      Array.from(description.children).forEach(node => {
        if (text(node) !== PHONE_HELP) return;
        booking = row('Timebestilling');
        const help = document.createElement('p');
        help.append('Finner du ikke en tid som passer? Ring ');
        const phone = document.createElement('a');
        phone.href = 'tel:+4745801000';
        phone.textContent = '458 01 000';
        help.append(phone, '. Vi har også enkelte timer som ikke vises på nett.');
        booking.content.append(help);
        node.remove();
      });
    }

    // Care advice: only known top-level headings start a group; the nodes after them move intact.
    let care = null;
    if (featured) {
      let target = null;
      let intro = null;
      Array.from(featured.children).forEach(node => {
        const value = text(node);
        if (CARE_INTRO.test(value)) { intro = node; return; }
        if (CARE_HEADING.test(value)) {
          if (!care) care = row('Før og etter behandling');
          if (intro) { care.content.append(intro); intro = null; }
          // A styled paragraph, as in the source text: no heading level to skip under the page's H1.
          const heading = document.createElement('p');
          heading.className = 'll-treatment-panels__subheading';
          heading.textContent = /^Før/i.test(value) ? 'Før behandling' : 'Etter behandling';
          care.content.append(heading);
          target = care.content;
          node.remove();
        } else if (target) {
          target.append(node);
        }
      });
    }

    // Place the rows: care after the explanation (or in its place when nothing else was left), then booking help.
    const anchor = explanation && featured && text(featured) ? explanation : null;
    let after = anchor || explanation || root.querySelector('[data-treatment-overview]') || root.lastElementChild;
    [care, booking].filter(Boolean).forEach(group => {
      if (after && after.parentNode === root) after.after(group.item);
      else root.append(group.item);
      after = group.item;
    });
    if (explanation && featured && !text(featured)) explanation.remove();

    // The band under the product (sections/laserlab-treatment-story) tells «Slik virker behandlingen» open, so the closed
    // row leaves the column. Only the row marked for it, only when every block left in it is in the band, and never the
    // care or booking rows. Section hidden or no script: the row stays, as before.
    // Improvement samlet_tekst (11.10): the band tells the treatment once, with the description, and leaves out the
    // page's introduction because the description says the same; the row goes with it. The care advice has already
    // moved to its own row above, so it stays. One exception (review 11.10, Smooth Eye: «Kommer du 30 minutter før
    // timen, kan du få bedøvelseskrem.»): a sentence of the introduction written to the reader (du, deg, din) that the
    // page does not say elsewhere keeps the row, so practical information is never lost. «Elsewhere» is the band, the
    // intro under the name and the facts, compared on words of four letters or more by their first five letters (as
    // the band's own checks); fewer than half found is not said. Course and interval sentences («Vi anbefaler en kur
    // …», «… hver 4. uke») do not count: the facts show Anbefalt antall and Hvor ofte.
    const story = document.querySelector('.ll-story');
    if (story && explanation && explanation.isConnected && explanation.hasAttribute('data-story-intro') && featured) {
      const told = text(story);
      const samlet = story.hasAttribute('data-ll-samlet') && (document.body.dataset.llFlags || '').split(' ').includes('samlet_tekst');
      if (samlet) {
        const column = root.closest('[id^="MainProduct-"]') || document;
        const said = [story, ...column.querySelectorAll('.ll-intro-first, .ll-treatment-details, .product__price-heading')].map(text).join(' ').toLowerCase();
        const untold = node => text(node).replace(/([.!?])\s+(?=[A-ZÆØÅ«])/g, '$1\n').split('\n').some(sentence => {
          if (!READER.test(sentence) || COURSE.test(sentence)) return false;
          const stems = [...new Set(sentence.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(word => word.length >= 4).map(word => word.slice(0, 5)).filter(stem => !STOP.has(stem)))];
          return stems.length > 0 && stems.filter(stem => said.includes(stem)).length * 2 < stems.length;
        });
        if (!Array.from(featured.children).some(untold)) explanation.remove();
      } else if (Array.from(featured.children).every(node => told.includes(text(node)))) explanation.remove();
      // Nothing left in the list: no empty rule under the facts.
      if (!root.querySelector('details')) {
        const more = root.closest('.ll-treatment-more');
        if (more && !text(more)) more.remove();
      }
    }
  }

  const init = scope => scope.querySelectorAll('[data-treatment-content]').forEach(enhance);
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
